import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const SearchContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0052CC 0%, #0747A6 100%)',
  padding: theme.spacing(4, 0),
  marginBottom: theme.spacing(4),
  color: 'white',
}));

const FilterCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const JobCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

const mockJobs = [
  {
    id: 1,
    title: 'Senior Frontend Developer',
    company: 'Tech Corp',
    location: 'New York, NY',
    type: 'Full-time',
    salary: '$80k-$120k',
    posted: '2 days ago',
    description: 'We are looking for an experienced Frontend Developer...',
    tags: ['React', 'TypeScript', 'Node.js'],
  },
  {
    id: 2,
    title: 'Product Manager',
    company: 'Innovation Inc',
    location: 'Remote',
    type: 'Full-time',
    salary: '$90k-$130k',
    posted: '1 week ago',
    description: 'Join our product team to drive innovation...',
    tags: ['Product', 'Agile', 'UX'],
  },
  // Add more mock jobs as needed
];

function SearchPage() {
  const [filters, setFilters] = useState({
    jobType: '',
    experience: '',
    salary: '',
    datePosted: '',
  });

  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value,
    });
  };

  return (
    <Box>
      <SearchContainer>
        <Container>
          <Typography variant="h4" gutterBottom>
            Find Your Dream Job
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Job title, keywords, or company"
                variant="outlined"
                sx={{ bgcolor: 'white', borderRadius: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Location"
                variant="outlined"
                sx={{ bgcolor: 'white', borderRadius: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Container>
      </SearchContainer>

      <Container>
        <Grid container spacing={3}>
          {/* Filters Sidebar */}
          <Grid item xs={12} md={3}>
            <FilterCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Filters
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Job Type</InputLabel>
                    <Select
                      name="jobType"
                      value={filters.jobType}
                      onChange={handleFilterChange}
                      label="Job Type"
                    >
                      <MenuItem value="full-time">Full Time</MenuItem>
                      <MenuItem value="part-time">Part Time</MenuItem>
                      <MenuItem value="contract">Contract</MenuItem>
                      <MenuItem value="internship">Internship</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Experience Level</InputLabel>
                    <Select
                      name="experience"
                      value={filters.experience}
                      onChange={handleFilterChange}
                      label="Experience Level"
                    >
                      <MenuItem value="entry">Entry Level</MenuItem>
                      <MenuItem value="mid">Mid Level</MenuItem>
                      <MenuItem value="senior">Senior Level</MenuItem>
                      <MenuItem value="lead">Lead</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Salary Range</InputLabel>
                    <Select
                      name="salary"
                      value={filters.salary}
                      onChange={handleFilterChange}
                      label="Salary Range"
                    >
                      <MenuItem value="0-50k">$0 - $50k</MenuItem>
                      <MenuItem value="50k-100k">$50k - $100k</MenuItem>
                      <MenuItem value="100k-150k">$100k - $150k</MenuItem>
                      <MenuItem value="150k+">$150k+</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Date Posted</InputLabel>
                    <Select
                      name="datePosted"
                      value={filters.datePosted}
                      onChange={handleFilterChange}
                      label="Date Posted"
                    >
                      <MenuItem value="1">Last 24 hours</MenuItem>
                      <MenuItem value="7">Last 7 days</MenuItem>
                      <MenuItem value="30">Last 30 days</MenuItem>
                      <MenuItem value="90">Last 90 days</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </FilterCard>
          </Grid>

          {/* Job Listings */}
          <Grid item xs={12} md={9}>
            <Typography variant="h6" gutterBottom>
              {mockJobs.length} Jobs Found
            </Typography>
            {mockJobs.map((job) => (
              <JobCard key={job.id}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {job.title}
                  </Typography>
                  <Typography color="textSecondary" gutterBottom>
                    {job.company}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      <LocationOnIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {job.location}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <WorkIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {job.type}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <AttachMoneyIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {job.salary}
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    {job.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {job.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Posted {job.posted}
                    </Typography>
                    <Button variant="outlined" color="primary">
                      Apply Now
                    </Button>
                  </Box>
                </CardContent>
              </JobCard>
            ))}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default SearchPage; 